import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react'
import { Button, Form, Result, Skeleton, Space } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import B2BLayout from '../../../layouts/b2b'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { TablePaginationPosition } from '../../deliveries/list/interface'
import { objectToUrl } from '../../utils'
import { currentFiltersProps, IData, IEnumsList, ISorterProps } from './interfaces'
import { filtersList } from './filters'
import { getColumns } from './columns'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import type { EditableColumnType } from '../../../components/data-table/interface'
import { useSelector } from 'react-redux'
import { IDeleteRecord } from '../../claims-request/list/interfaces'
import { createSelector } from 'reselect'
import { FileExcelOutlined, FilterOutlined } from '@ant-design/icons'
import modalClaimsExport from '../../../views/modal-export-claims'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import { ClaimsPrintButton } from '../ui/claims-print-button/claims-print-button'
import FiltersList from '../../../components/v2/filters'
import { FiltersFastButtons } from '../../../components/v2/filters-fast-buttons'
import DataTable from '../../../components/data-table'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { useMediaQuery } from 'react-responsive'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { getMobileFields } from './mobile-fields'
import { ResultStatusType } from 'antd/lib/result'
import { canDeleteClaimsLikeGoLeader } from 'src/react-app/lib/permissions'

type TableRecord = { id: React.Key; type?: string | null } & Record<string, any>

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

const ClaimsRequestsList = () => {
  const [filtersForm] = Form.useForm()
  const [data, setData] = useState<IData[]>([])
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [sorter, setSorter] = useState<ISorterProps>({})
  const navigate = useNavigate()
  const [availableFilters, setAvailableFilters] = useState<IEnumsList>({})
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: 50,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})
  const [stages, setStages] = useState<any>(null)
  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const isLoadingStagesRef = useRef(false)
  const isFirstLoadCompleteRef = useRef(false)

  const { sessionData } = useSelector(createSession)
  const isLeader = useMemo(
    () => canDeleteClaimsLikeGoLeader(sessionData?.permissionsWithScope),
    [sessionData?.permissionsWithScope],
  )
  const { search } = useLocation()

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const getData = useCallback(() => {
    if (!isFirstLoadCompleteRef.current) {
      setLoading(true)
    } else {
      setTableLoading(true)
    }
    setError(null)
    const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))
    fetchAPI(`/api/list/claims${objectToUrl(urlParamsObj)}`)
      .then(({ data, pagination, sorter, enums }) => {
        if (data) {
          setData(data)
        }
        if (pagination) {
          setPagination(prevState => ({
            ...prevState,
            ...pagination,
          }))
        }
        if (sorter) {
          setSorter(sorter)
        }
        if (enums) {
          setAvailableFilters(enums)
        }
        if (!isFirstLoadCompleteRef.current) {
          isFirstLoadCompleteRef.current = true
        }
      })
      .catch(e => {
        console.error({ e })
        setError({
          message: (e as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
        })
      })
      .finally(() => {
        setLoading(false)
        setTableLoading(false)
      })
  }, [location.search])

  useEffect(() => {
    if (!stages) {
      getStages()
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0) {
      getData()
    }
  }, [location.search, stages])

  const isMobile = useMediaQuery({ maxWidth: 768 })
  const { setHeaderActions } = useHeaderActions()

  const breadcrumbs = [
    {
      href: '/claims',
      title: 'Рекламации',
    },
  ]

  const parseUrlSearchParams = (search: string): currentFiltersProps => {
    const urlParams = new URLSearchParams(search)
    return {
      created_at: urlParams.get('created_at') || undefined,
      date_application: urlParams.get('date_application') || undefined,
      stage: urlParams.get('stage') || undefined,
      dealer_code: urlParams.get('dealer_code') || undefined,
      address: urlParams.get('address') || undefined,
      name: urlParams.get('name') || undefined,
      phone: urlParams.get('phone') || undefined,
      classification_id: urlParams.get('classification_id') || undefined,
      dealer_requirement_id: urlParams.get('dealer_requirement_id') || undefined,
      num_tech: urlParams.get('num_tech') || undefined,
      id: urlParams.get('id') || undefined,
    }
  }

  const changeFiltersHandler = useCallback(
    async (filters: any) => {
      console.log('filters', filters)
      let tmpFilter = { ...(filters || {}) }
      if (tmpFilter?.stage?.length === 0) {
        delete tmpFilter.stage
      }
      if (tmpFilter?.dealer_code?.length === 0) {
        delete tmpFilter.dealer_code
      }
      if (tmpFilter?.classification_id?.length === 0) {
        delete tmpFilter.classification_id
      }
      if (tmpFilter?.dealer_requirement_id?.length === 0) {
        delete tmpFilter.dealer_requirement_id
      }
      if (pagination?.pageSize !== 10) {
        tmpFilter.pageSize = pagination?.pageSize
      }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      {
        navigate({ search: newUrlParams })
      }
      setData([])
      getData()
    },
    [pagination.pageSize, getData, history, setData],
  )

  useEffect(() => {
    const newFilters = parseUrlSearchParams(location.search)
    setCurrentFilters(newFilters)
  }, [location.search])

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ...pagination }))
      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate, pagination],
  )

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('measurementsPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 10) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  const getFilters = useMemo(() => {
    return filtersList(currentFilters, availableFilters)
  }, [currentFilters, availableFilters])

  const { pathname } = useLocation()
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))

  const getStages = useCallback(() => {
    if (!stages && !isLoadingStagesRef.current) {
      isLoadingStagesRef.current = true
      fetchAPI('/api/get-stages/claim')
        .then(data => {
          setStages(data)
          isLoadingStagesRef.current = false
        })
        .catch(() => {
          console.error('Не удалось получить стадии')
          setError({ code: 'error', message: 'Не удалось получить стадии' })
          isLoadingStagesRef.current = false
        })
    }
  }, [stages])

  const deleteRecord = useCallback(
    async (id: number, removeRequest = false): Promise<IDeleteRecord> => {
      try {
        const response = await fetchAPI(`/api/claim/${id}`, {
          method: 'DELETE',
          body: removeRequest ? JSON.stringify({ remove_request: true }) : undefined,
        })
        if (response?.success === true) {
          const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))
          await fetchAPI(`/api/list/claims${objectToUrl(urlParamsObj)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData }) => {
              if (data) {
                setData(data)
              }
              if (paginationData) {
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
              }
              if (sorterData) {
                setSorter(sorterData)
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        } else {
          console.error('Ошибка при удалении записи')
          return { error: true }
        }
      } catch (error) {
        console.error('Ошибка при отправке запроса на удаление:', error)
        return { error: true }
      }
      return { success: true }
    },
    [location.search],
  )

  const columns = useMemo(
    () =>
      (getColumns(sorter, isLeader, deleteRecord) as unknown) as EditableColumnType<TableRecord>[],
    [sorter, isLeader, deleteRecord],
  )

  const viewColumns = useMemo<EditableColumnType<TableRecord>[]>(() => {
    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const result: EditableColumnType<TableRecord>[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = columns.find(column => column.dataIndex === columnOrder[i])
        if (item) {
          result.push(item)
        }
      }
      const actionColumn = columns.find(column => column.key === 'operation')
      if (actionColumn && !result.find(col => col?.key === 'operation')) {
        result.push(actionColumn)
      }
      return result
    }
    return columns
  }, [columns, jsonSettings])

  const settingsColumns = useMemo(
    () =>
      columns
        .filter((column: any) => column?.key !== 'operation')
        .map(column => ({
          key: (column as any)?.dataIndex,
          title: column?.title,
        })),
    [columns],
  ) as TransferItem[]

  function downloadUrl(url, exportName) {
    const downloadAnchorNode = document.createElement('a')
    downloadAnchorNode.setAttribute('href', url)
    downloadAnchorNode.setAttribute('download', exportName)
    document.body.appendChild(downloadAnchorNode)
    downloadAnchorNode.click()
    downloadAnchorNode.remove()
  }

  const onPrint = useCallback(async () => {
    const result = await modalClaimsExport()
    if (result === null) {
      return
    }
    downloadUrl(`/api/claims/statistics?year=${result.year}`, `Статистика.xlsx`)
  }, [])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/list/claims', new URLSearchParams(location.search))
  }, [location.search])

  const mobileFields = useMemo(() => getMobileFields(), [])

  useEffect(() => {
    const actions: HeaderAction[] = []
    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }
    setHeaderActions(actions)
    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, isMobile])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Рекламации'}>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!loading && !error && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <Button onClick={onPrint}>
                    <FileExcelOutlined /> Статистика
                  </Button>
                  <FiltersList
                    filters={getFilters
                      .filter(f => ['id'].includes(f.name))
                      .map(f => (f.name === 'id' ? { ...f, placeholder: 'Найти рекламацию' } : f))
                      .sort((a, b) => (a.name === 'id' ? -1 : b.name === 'id' ? 1 : 0))}
                    onChange={changeFiltersHandler}
                    isChangeByButtons
                    form={filtersForm}
                  />
                </Space>
                <FiltersDrawer
                  filters={getFilters}
                  onChange={changeFiltersHandler}
                  currentFilters={currentFilters}
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              isMobile ? (
                <FiltersFastButtons
                  filters={(stages || []).map((s: any) => ({
                    name: s.name,
                    code: 'stage',
                    value: s.code,
                    style: (s as any).style,
                  }))}
                  onChange={changeFiltersHandler}
                />
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  <FiltersFastButtons
                    filters={(stages || []).map((s: any) => ({
                      name: s.name,
                      code: 'stage',
                      value: s.code,
                      style: (s as any).style,
                    }))}
                    onChange={changeFiltersHandler}
                  />
                  <Space align={'center'} wrap>
                    <ClaimsPrintButton path={excelPath} columns={viewColumns} />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
                  </Space>
                </Space>
              )
            }
          />
          <DataTable
            columns={viewColumns}
            mobileFields={mobileFields}
            data={data}
            loading={tableLoading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'claim'}
            pagination={pagination}
            onRowClick={record => navigate(`/claim/${record.id}`)}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            headerRef={headerRef}
            bordered
            scroll={{ x: 'max-content' }}
            maxLines={4}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default memo(ClaimsRequestsList)
